/**
 * VistoChat Widget — production-grade cross-domain chat loader
 *
 * Customer install:
 *   <script src="https://widget.vistochat.com/v1/widget.js"
 *           data-bot-id="bot_public_id"
 *           data-locale="el"                       (optional; auto-detected from <html lang>)
 *           data-position="bottom-right"           (optional; default bottom-right)
 *           data-color="#101475"                   (optional; default from bot config server-side)
 *           data-user-jwt="..."                    (optional; for authenticated bots — B/C modes)
 *           data-launcher="auto"                   (optional; auto|manual — manual = no launcher button)
 *           defer></script>
 *
 * Programmatic API (after script loads):
 *   window.VistoChat.open() / close() / toggle()
 *   window.VistoChat.send(text)             — send a message programmatically
 *   window.VistoChat.destroy()              — fully unmount, no DOM trace
 *   window.VistoChat.on(event, callback)    — subscribe to events
 *   window.VistoChat.off(event, callback)
 *   window.VistoChat.isReady()
 *
 * DOM events (dispatched on document):
 *   vistochat:ready          — widget mounted, iframe loaded
 *   vistochat:opened         — chat panel opened
 *   vistochat:closed         — chat panel closed
 *   vistochat:message:user   — { detail: { text } }
 *   vistochat:message:bot    — { detail: { text } }
 *   vistochat:lead:captured  — { detail: { email?, phone?, name? } }
 *   vistochat:error          — { detail: { message } }
 *
 * Defensive guarantees:
 *   • Shadow DOM isolation (host CSS can't reach widget styles, and v.v.)
 *   • iframe sandbox + strict referrer policy
 *   • No globals other than `window.VistoChat`
 *   • Idempotent loading (second script tag is no-op)
 *   • Respects prefers-reduced-motion + prefers-color-scheme
 */

type Position =
  | 'bottom-right' | 'bottom-left' | 'bottom-center'
  | 'top-right'    | 'top-left'    | 'top-center';

interface WidgetConfig {
  botId: string;
  apiUrl: string;
  position: Position;
  launcherStyle: 'bubble' | 'preview' | 'pill' | 'avatar';
  /** When launcherStyle === 'avatar', this holds the WebP face URL; the
   *  widget derives a PNG fallback by replacing .webp → .png. Null in any
   *  other launcher style. */
  launcherAvatar: string | null;
  color: string;
  locale: string;
  userJwt: string | null;
  launcher: 'auto' | 'manual';
  teaserText: string | null;
}

type EventName =
  | 'ready' | 'opened' | 'closed'
  | 'message:user' | 'message:bot'
  | 'lead:captured' | 'error'
  | 'teaser:click';

interface VistoChatAPI {
  open(): void;
  close(): void;
  toggle(): void;
  send(text: string): void;
  destroy(): void;
  on(event: EventName, cb: (detail?: any) => void): void;
  off(event: EventName, cb: (detail?: any) => void): void;
  isReady(): boolean;
  config(updates: Partial<WidgetConfig>): void;
}

declare global {
  interface Window { VistoChat?: VistoChatAPI }
}

// ---- Idempotent guard ----
if (window.VistoChat) {
  // Already loaded — second <script> tag is a no-op.
  // (Helps prevent duplicate widgets when SPA navigation re-injects script.)
} else {
  bootstrap();
}

function bootstrap() {
  const cfg = readConfigFromScript();
  if (!cfg) return;

  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  const darkMode = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;

  const listeners = new Map<EventName, Set<(d?: any) => void>>();
  let isOpen = false;
  let isReady = false;
  let host: HTMLDivElement | null = null;
  let shadow: ShadowRoot | null = null;
  let launcher: HTMLButtonElement | null = null;
  let frame: HTMLIFrameElement | null = null;
  let skeleton: HTMLDivElement | null = null;
  let mediaQueryListener: ((e: MediaQueryListEvent) => void) | null = null;
  // Animation chosen by the bot owner (set via postMessage from embed iframe
  // after handshake). Default 'slide-up' applies until the iframe loads.
  type AnimationStyle = 'slide-up' | 'fade' | 'bounce' | 'none';
  let currentAnimation: AnimationStyle = 'slide-up';

  // Teaser tooltip — set via postMessage 'set-teaser' from iframe after handshake.
  // localStorage key tracks per-bot dismissal so we don't re-show after user closed it.
  let teaserEl: HTMLDivElement | null = null;
  let teaserTimer: ReturnType<typeof setTimeout> | null = null;
  const TEASER_DISMISSED_KEY = `vistochat:teaser-dismissed:${cfg.botId}`;
  // 28/09/2026 (owner, mobile): the balloon never came back on a phone. Two
  // causes: (1) the AUTOMATIC open counted as "seen" and dismissed it FOREVER,
  // so from the second visit on no visitor ever saw it; (2) a dismissal never
  // expired. Now only a visitor's own action dismisses it, for 7 days. A legacy
  // '1' (almost always written by the auto-open) counts as not dismissed.
  const TEASER_DISMISS_MS = 7 * 24 * 3600 * 1000;
  function teaserDismissedRecently(): boolean {
    try {
      const v = localStorage.getItem(TEASER_DISMISSED_KEY);
      const ts = v ? Number(v) : 0;
      return ts > 1e12 && Date.now() - ts < TEASER_DISMISS_MS;
    } catch { return false; }
  }
  // Auto-open at most once a day per visitor. Before, it fired on EVERY page view
  // until the visitor explicitly closed the chat — on a phone that is the whole
  // screen covered again on each page they navigate to.
  const AUTO_OPENED_KEY = `vistochat:auto-opened:${cfg.botId}`;
  const AUTO_OPEN_EVERY_MS = 24 * 3600 * 1000;
  // Last appearance the server sent (colour, position, launcher face), so a repeat
  // visitor sees the right launcher at once instead of after the handshake.
  const LOOK_KEY = `vistochat:look:${cfg.botId}`;
  // Balloon sequence (27/09): the server sends the live messages — welcome,
  // review score, price claim — and they play ONCE, stopping on the last. Every
  // message opens the chat; none links anywhere else (owner rule).
  type SeqKind = 'welcome' | 'reviews' | 'prices';
  type SeqItem = { kind: SeqKind; text: string; ask: string | null };
  const SEQ_STEP_MS = 7000;
  let seqItems: SeqItem[] = [];
  let seqIndex = 0;
  let seqTimer: ReturnType<typeof setTimeout> | null = null;
  let seqPaused = false;
  let seqRemaining = 0;
  let seqStartedAt = 0;

  function emit(name: EventName, detail?: any) {
    // Internal listeners
    listeners.get(name)?.forEach(cb => { try { cb(detail); } catch { /* swallow */ } });
    // DOM event for analytics (GTM, etc.)
    document.dispatchEvent(new CustomEvent(`vistochat:${name}`, { detail }));
  }

  function postToFrame(msg: any) {
    if (!frame?.contentWindow) return;
    try { frame.contentWindow.postMessage({ ...msg, __vc: true }, new URL(cfg.apiUrl).origin); } catch { /* */ }
  }

  /** Loader chrome in the visitor's language. Everything else the visitor reads
   *  comes from the iframe, which is already localised; these few labels and
   *  messages were Greek on every site, English ones included. */
  const UI: Record<string, { el: string; en: string }> = {
    open:     { el: 'Άνοιγμα chat',     en: 'Open chat' },
    closeChat:{ el: 'Κλείσιμο chat',    en: 'Close chat' },
    close:    { el: 'Κλείσιμο',         en: 'Close' },
    loading:  { el: 'Φορτώνει…',        en: 'Loading…' },
    failed:   { el: 'Πρόβλημα φόρτωσης. Δοκιμάστε ξανά αργότερα.', en: 'Could not load. Please try again later.' },
    help:     { el: 'Πώς μπορώ να σας βοηθήσω;', en: 'How can I help you?' },
    say:      { el: 'Πες κάτι',         en: 'Say something' },
    write:    { el: 'Γράψτε ένα μήνυμα…', en: 'Type a message…' },
  };
  function ui(k: keyof typeof UI): string {
    return String(cfg?.locale || '').toLowerCase().startsWith('el') ? UI[k].el : UI[k].en;
  }

  /** Attribute-safe HTML escape. Used for trusted server-supplied URLs/text
   *  that we still want to neutralise just in case (defense in depth — the
   *  server already validates URLs server-side). */
  function escAttr(s: string): string {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function buildStyles(): string {
    // Animation comes from the bot's widget_animation setting (handshake → embed
    // → postMessage 'set-animation'). Falls back to 'slide-up' until the iframe
    // sends its preference. `reducedMotion` from the user's OS always wins.
    const animation = currentAnimation;

    // Anchor the launcher + frame-wrap in one of 6 positions
    let anchor = 'right:20px;bottom:20px;';
    let frameAnchor = 'right:20px;bottom:88px;';
    let originY = '8px';
    switch (cfg.position) {
      case 'bottom-right':  anchor = 'right:20px;bottom:20px;'; frameAnchor = 'right:20px;bottom:88px;'; break;
      case 'bottom-left':   anchor = 'left:20px;bottom:20px;';  frameAnchor = 'left:20px;bottom:88px;';  break;
      case 'bottom-center': anchor = 'left:50%;bottom:20px;transform:translateX(-50%);'; frameAnchor = 'left:50%;bottom:88px;transform:translateX(-50%);'; break;
      case 'top-right':     anchor = 'right:20px;top:20px;';    frameAnchor = 'right:20px;top:88px;';    originY = '-8px'; break;
      case 'top-left':      anchor = 'left:20px;top:20px;';     frameAnchor = 'left:20px;top:88px;';     originY = '-8px'; break;
      case 'top-center':    anchor = 'left:50%;top:20px;transform:translateX(-50%);'; frameAnchor = 'left:50%;top:88px;transform:translateX(-50%);'; originY = '-8px'; break;
    }
    const centered = cfg.position.endsWith('-center');
    const baseTranslate = centered ? 'translateX(-50%)' : '';

    // Compute open/closed transform + transition timing per animation type.
    // reducedMotion (OS-level "Reduce motion") always degrades to 'none'.
    let closedTransform: string, openTransform: string, transition: string;
    if (reducedMotion || animation === 'none') {
      closedTransform = `${baseTranslate} translateY(0) scale(1)`.trim();
      openTransform   = closedTransform;
      transition = 'none';
    } else if (animation === 'fade') {
      // Pure opacity fade — no translate / scale. Slightly longer for the
      // "soft" feel.
      closedTransform = `${baseTranslate} translateY(0) scale(1)`.trim();
      openTransform   = closedTransform;
      transition = 'opacity .25s ease';
    } else if (animation === 'bounce') {
      closedTransform = `${baseTranslate} translateY(${originY}) scale(0.6)`.trim();
      openTransform   = `${baseTranslate} translateY(0) scale(1)`.trim();
      // Spring-y cubic-bezier — overshoot then settle.
      transition = 'transform .35s cubic-bezier(.34,1.56,.64,1), opacity .2s ease';
    } else {
      // slide-up (default)
      closedTransform = `${baseTranslate} translateY(${originY}) scale(0.98)`.trim();
      openTransform   = `${baseTranslate} translateY(0) scale(1)`.trim();
      transition = 'transform .18s ease, opacity .18s ease';
    }

    return `
      :host { all: initial !important; }
      * { box-sizing: border-box; }

      /* ==== Launcher: bubble style ====
         pointer-events:auto undoes the host's pointer-events:none (which lets
         page clicks pass through everything except the launcher + open frame). */
      .vc-launcher {
        position: fixed; ${anchor}
        width: 56px; height: 56px; border-radius: 50%;
        background: ${cfg.color}; color: #fff;
        display: flex; align-items: center; justify-content: center;
        box-shadow: 0 8px 24px -6px rgba(0,0,0,.3);
        cursor: pointer; border: none; outline: none;
        pointer-events: auto;
        transition: transform .15s ease;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      }
      .vc-launcher:hover { transform: ${centered ? 'translateX(-50%) ' : ''}scale(1.05); }
      .vc-launcher:focus-visible { box-shadow: 0 8px 24px -6px rgba(0,0,0,.3), 0 0 0 4px rgba(255,255,255,.4), 0 0 0 6px ${cfg.color}; }
      .vc-launcher svg { width: 26px; height: 26px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
      .vc-launcher.hidden { display: none; }

      /* ==== Launcher: pill style (wider with text CTA) ==== */
      .vc-launcher.style-pill {
        width: auto; height: 48px; border-radius: 24px;
        padding: 0 18px 0 14px; gap: 10px;
        font-size: 14px; font-weight: 600;
      }
      .vc-launcher.style-pill svg { width: 20px; height: 20px; }

      /* ==== Launcher: avatar (face photo) style ==========================
         Slightly larger than the bubble to make the face readable from the
         corner of the screen. The <picture> contains a WebP source + PNG
         fallback; both crop to the upper third via object-position so the
         eyes always sit centered in the visible circle. A subtle pulsing
         ring draws attention without being annoying — disabled when the
         visitor's OS prefers reduced motion. */
      .vc-launcher.style-avatar {
        width: 64px; height: 64px;
        background: transparent;
        padding: 0;
        overflow: visible;       /* lets the pulse ring extend beyond */
        box-shadow: 0 8px 24px -6px rgba(0,0,0,.35);
      }
      .vc-launcher.style-avatar picture {
        display: block; width: 100%; height: 100%;
        position: relative;        /* establish stacking context */
        z-index: 2;                /* above the rotated-square tail (::before) */
      }
      .vc-launcher.style-avatar .vc-launcher-img {
        width: 100%; height: 100%;
        border-radius: 50%;
        object-fit: cover;
        object-position: center 25%;   /* upper-third — face composition fix */
        border: 3px solid #fff;
        box-sizing: border-box;
        background: ${cfg.color};      /* shows during image load */
        display: block;
      }
      .vc-launcher.style-avatar:hover { transform: ${centered ? 'translateX(-50%) ' : ''}scale(1.08); }
      .vc-launcher.style-avatar .vc-launcher-pulse {
        position: absolute; inset: 0;
        border-radius: 50%;
        border: 2px solid ${cfg.color};
        opacity: 0;
        pointer-events: none;
        animation: vc-launcher-pulse 2.4s ease-out infinite;
      }
      @keyframes vc-launcher-pulse {
        0%   { transform: scale(0.95); opacity: 0.6; }
        70%  { transform: scale(1.30); opacity: 0; }
        100% { transform: scale(1.30); opacity: 0; }
      }
      @media (prefers-reduced-motion: reduce) {
        .vc-launcher.style-avatar .vc-launcher-pulse { animation: none; opacity: 0; }
      }

      /* ==== Speech-bubble tail ==========================================
         Plain (NOT rotated) white square tucked into the ${cfg.position.endsWith('-left') ? 'SW' : 'SE'} quadrant of
         the launcher, sized + positioned so 3 of its 4 corners sit well
         INSIDE the circle's radius (hidden behind the photo) while only
         the ${cfg.position.endsWith('-left') ? 'SW' : 'SE'} corner peeks past the circle's perimeter as a
         single clean talk-balloon tail. No rotation = no other curves
         show. Tail sits behind the picture (z-index:1 vs picture's 2). */
      .vc-launcher-balloon-tail {
        position: absolute;
        bottom: 2px;
        ${cfg.position.endsWith('-left') ? 'left: 2px;' : 'right: 2px;'}
        width: 28px; height: 28px;
        background: #fff;
        border-radius: ${cfg.position.endsWith('-left') ? '0 0 0 5px' : '0 0 5px 0'};
        box-shadow: ${cfg.position.endsWith('-left') ? '-1px' : '1px'} 1px 3px rgba(0,0,0,.20);
        z-index: 1;
        pointer-events: none;
      }

      /* ==== Launcher: preview style (mini chat window pre-input) ==== */
      .vc-launcher.style-preview {
        width: 320px; height: auto; border-radius: 14px;
        background: #fff; color: var(--vc-text, #1e293b);
        padding: 14px; gap: 10px;
        display: flex; flex-direction: column; align-items: stretch;
        text-align: left;
        box-shadow: 0 12px 28px -8px rgba(0,0,0,.25);
      }
      .vc-launcher.style-preview .vc-prev-head { display: flex; align-items: center; gap: 10px; }
      .vc-launcher.style-preview .vc-prev-bubble {
        background: ${cfg.color}; color: #fff;
        width: 36px; height: 36px; border-radius: 50%;
        display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
      }
      .vc-launcher.style-preview .vc-prev-bubble svg { width: 18px; height: 18px; }
      .vc-launcher.style-preview .vc-prev-text {
        flex: 1; font-size: 13px; color: #64748b; line-height: 1.4;
      }
      .vc-launcher.style-preview .vc-prev-input {
        margin-top: 4px;
        background: #f1f5f9; border: 1px solid #e2e8f0;
        border-radius: 10px; padding: 10px 14px;
        font-size: 13px; color: #94a3b8; text-align: left;
      }

      /* ==== Frame ==== */
      /* IMPORTANT: no background on the wrap. The iframe is fully opaque, so a
         white wrap background only shows through the 1px anti-aliased rim of
         the rounded corner — manifesting as small white "dots" at all 4 corners
         where the colored header/input-bar meet the rounded clip. Transparent
         wrap + border-radius on the iframe itself = clean clipped corners. */
      .vc-frame-wrap {
        position: fixed; ${frameAnchor}
        width: 380px; height: min(640px, calc(100vh - 120px));
        border-radius: 14px; overflow: hidden;
        box-shadow: 0 24px 64px -16px rgba(0,0,0,.35);
        background: transparent;
        transform: ${closedTransform}; opacity: 0;
        /* When closed: pointer-events: none so the panel doesn't intercept clicks.
           When open: pointer-events: auto. host has pointer-events: none. */
        pointer-events: none;
        transition: ${transition};
        will-change: transform, opacity;
      }
      .vc-frame-wrap.open { transform: ${openTransform}; opacity: 1; pointer-events: auto; }

      .vc-frame {
        width: 100%; height: 100%; border: 0; display: block;
        background: #fff;
        border-radius: 14px;   /* match the wrap; double-clips the iframe content
                                  so colored regions are clipped at the iframe
                                  boundary itself, not just at the wrap. */
      }

      .vc-skeleton {
        position: absolute; inset: 0;
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        gap: 12px;
        background: linear-gradient(180deg, #fafbfd 0%, #f0f2f5 100%);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        color: #64748b;
      }
      .vc-skeleton.gone { display: none; }
      .vc-spinner {
        width: 28px; height: 28px; border-radius: 50%;
        border: 3px solid #e2e8f0; border-top-color: ${cfg.color};
        animation: vc-spin 0.8s linear infinite;
      }
      .vc-skeleton-text { font-size: 13px; }
      @keyframes vc-spin { to { transform: rotate(360deg); } }

      @media (max-width: 480px) {
        .vc-frame-wrap {
          width: auto; height: auto;
          left: 12px; right: 12px; bottom: 12px; top: 12px;
          border-radius: 14px;
        }
        .vc-launcher { bottom: 16px; ${cfg.position === 'bottom-left' ? 'left:16px;' : 'right:16px;'} width: 52px; height: 52px; }
      }

      @media (prefers-reduced-motion: reduce) {
        .vc-launcher, .vc-frame-wrap { transition: none !important; }
        .vc-spinner { animation: none; }
        .vc-teaser { animation: none !important; }
        .vc-seq-body { transition: none !important; }
      }

      /* ==== Teaser tooltip — speech bubble above the launcher to draw attention.
         Positioned with the same anchor as the iframe (88px gap from edge). ==== */
      .vc-teaser {
        position: fixed; ${frameAnchor}
        max-width: 260px;
        padding: 10px 32px 10px 14px;
        background: #fff;
        color: #1e293b;
        border-radius: 14px;
        box-shadow: 0 6px 20px -4px rgba(0,0,0,.25), 0 2px 6px rgba(0,0,0,.08);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 13.5px; line-height: 1.4; font-weight: 500;
        pointer-events: auto;
        cursor: pointer;
        animation: vc-teaser-in .35s cubic-bezier(.34,1.56,.64,1) both,
                   vc-teaser-pulse 2.4s ease-in-out 1s 2;
      }
      /* Tail pointing down at the launcher (only for bottom-* positions) */
      ${cfg.position.startsWith('bottom-') ? `
      .vc-teaser::after {
        content: ''; position: absolute;
        ${cfg.position === 'bottom-right'  ? 'right: 22px;' : ''}
        ${cfg.position === 'bottom-left'   ? 'left: 22px;'  : ''}
        ${cfg.position === 'bottom-center' ? 'left: 50%; transform: translateX(-50%);' : ''}
        bottom: -7px;
        width: 14px; height: 14px;
        background: #fff;
        transform: ${cfg.position === 'bottom-center' ? 'translateX(-50%) rotate(45deg)' : 'rotate(45deg)'};
        box-shadow: 4px 4px 8px -4px rgba(0,0,0,.1);
      }` : ''}
      .vc-teaser-msg { display: block; }
      .vc-teaser-close {
        position: absolute; top: 4px; right: 6px;
        width: 22px; height: 22px;
        background: transparent; border: 0;
        color: #94a3b8; font-size: 18px; line-height: 1;
        cursor: pointer; border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        font-family: inherit;
      }
      .vc-teaser-close:hover { background: #f1f5f9; color: #1e293b; }
      .vc-teaser.vc-seq { max-width: 280px; }
      .vc-seq-body { transition: opacity .22s ease; }
      .vc-seq-out { opacity: 0; }
      .vc-seq-strong { font-weight: 600; }
      .vc-seq-ask {
        display: block; margin-top: 8px; padding: 6px 11px; border-radius: 8px; border: 0;
        background: ${cfg.color}; color: #fff; font: inherit; font-size: 12.5px; font-weight: 600;
        line-height: 1.3; text-align: left; cursor: pointer;
      }
      .vc-seq-ask:hover { filter: brightness(1.08); }
      .vc-seq-ask:focus-visible { outline: 2px solid ${cfg.color}; outline-offset: 2px; }
      .vc-teaser-close:focus-visible { outline: 2px solid ${cfg.color}; outline-offset: 1px; }

      @keyframes vc-teaser-in {
        from { opacity: 0; transform: translateY(8px) scale(0.95); }
        to   { opacity: 1; transform: translateY(0) scale(1); }
      }
      @keyframes vc-teaser-pulse {
        0%, 100% { transform: translateY(0) scale(1); }
        50%      { transform: translateY(-2px) scale(1.03); }
      }

      @media (max-width: 480px) {
        .vc-teaser { max-width: calc(100vw - 80px); font-size: 13px; }
      }
    `;
  }

  function mount() {
    if (host) return;

    host = document.createElement('div');
    host.setAttribute('data-vistochat-widget', '');
    // Defense in depth — three layers, in order of "most decisive" first:
    //   1) popover="manual" + showPopover() → puts the host in the BROWSER TOP
    //      LAYER (above ALL z-index AND ancestor transforms/filters/contain).
    //      This is the only bullet-proof escape from a customer site that has
    //      transformed parents or uses <dialog>/popover for their own modals.
    //      Supported: Chrome 114+, Edge 114+, Firefox 125+, Safari 17+ (≥95% of
    //      sessions in 2026). Older browsers fall through to layer 2.
    //   2) z-index: 2147483647 (max signed int32) + isolation:isolate — the
    //      classic approach for older browsers without popover API.
    //   3) The shadow children (launcher + frame) are themselves position:fixed,
    //      so they always render to the viewport regardless of host size.
    // pointer-events:none on the host + auto on the children so the page's
    // background remains clickable (no invisible overlay swallowing clicks).
    // NOTE: avoid `all:initial` here — it overrides display to `inline`, which
    // breaks the Popover API (top layer needs a block-like host). Instead we
    // explicitly reset each property the customer's stylesheet might leak in.
    host.style.cssText = [
      'display:block!important',
      'position:fixed!important',
      'inset:0!important',
      'width:0!important','height:0!important',
      'margin:0!important','padding:0!important','border:0!important',
      'background:transparent!important',
      'overflow:visible!important',
      'z-index:2147483647!important',
      'isolation:isolate!important',
      'pointer-events:none!important',
      'color-scheme:light dark!important',
      'font:initial!important','line-height:initial!important',
    ].join(';');
    shadow = host.attachShadow({ mode: 'closed' });

    const style = document.createElement('style');
    style.textContent = buildStyles();
    shadow.appendChild(style);

    // Launcher — choose style
    launcher = document.createElement('button');
    launcher.type = 'button';
    launcher.setAttribute('aria-label', ui('open'));
    renderLauncherContent();
    launcher.addEventListener('click', () => toggle());
    shadow.appendChild(launcher);

    document.body.appendChild(host);

    // Promote the host into the browser top layer via the Popover API.
    // This GUARANTEES we render above any z-index AND any stacking context
    // created by ancestor transform/filter/contain on the customer's site.
    // We use popover="manual" so user interactions can't dismiss it (Esc key,
    // outside click) — we own when it's shown/hidden.
    try {
      if ('showPopover' in HTMLElement.prototype) {
        host.setAttribute('popover', 'manual');
        (host as any).showPopover();
      }
    } catch { /* popover not supported on this browser → fall back to z-index */ }

    // Listen for messages from the iframe
    window.addEventListener('message', onIframeMessage);

    // Track viewport changes for responsive adjustments inside iframe
    const mq = window.matchMedia('(max-width: 480px)');
    mediaQueryListener = (e) => postToFrame({ type: 'viewport', mobile: e.matches });
    mq.addEventListener?.('change', mediaQueryListener);

    // Fetch bot config (handshake) directly so we can show the teaser BEFORE
    // the user opens the chat. We don't keep the session — it's just the
    // cheapest path to get teaser_text per-locale without loading the iframe.
    fetchTeaserConfig();
  }

  /**
   * Paint the launcher from the CURRENT cfg. Extracted so the handshake can
   * re-render it once the PANEL's appearance arrives: the embed snippet now only
   * identifies the bot, so everything visual is owner-configured server-side.
   * Safe to call repeatedly — it fully rewrites className and innerHTML.
   */
  function renderLauncherContent(): void {
    if (!launcher) return;
    // Defensive fallback: avatar style requires a launcher_avatar URL — if
    // somehow missing, render a normal bubble. Keeps the widget working
    // even if the customer's bot config is half-set.
    const effectiveStyle =
      cfg.launcherStyle === 'avatar' && !cfg.launcherAvatar
        ? 'bubble'
        : cfg.launcherStyle;
    const styleClass =
      effectiveStyle === 'preview' ? ' style-preview'
      : effectiveStyle === 'pill' ? ' style-pill'
      : effectiveStyle === 'avatar' ? ' style-avatar'
      : '';
    launcher.className = 'vc-launcher' + styleClass + (cfg.launcher === 'manual' ? ' hidden' : '');
    const chatIcon =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">' +
      '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>' +
      '</svg>';
    if (effectiveStyle === 'preview') {
      launcher.innerHTML =
        '<div class="vc-prev-head">' +
          '<span class="vc-prev-bubble">' + chatIcon + '</span>' +
          '<span class="vc-prev-text">' + escAttr(cfg.teaserText || ui('help')) + '</span>' +
        '</div>' +
        '<div class="vc-prev-input">' + ui('write') + '</div>';
    } else if (effectiveStyle === 'pill') {
      launcher.innerHTML = chatIcon + '<span>' + escAttr(cfg.teaserText || ui('say')) + '</span>';
    } else if (effectiveStyle === 'avatar' && cfg.launcherAvatar) {
      // <picture> = WebP first, PNG fallback. The CSS clip-path circle is
      // applied via .vc-launcher.style-avatar so we don't need to touch the
      // <img> rules. object-position: center 25% is calibrated for upper-
      // third portrait composition (typical for 1024×1024 ChatGPT-generated
      // headshots — face sits at the top of the frame, not centered).
      // The SVG tail extends from bottom-right of the circle, pointing
      // down-right — turns the avatar into a speech-bubble silhouette.
      const webp = cfg.launcherAvatar;
      const png  = webp.replace(/\.webp(\?|#|$)/i, '.png$1');
      launcher.innerHTML =
        '<picture>' +
          '<source srcset="' + escAttr(webp) + '" type="image/webp">' +
          '<img class="vc-launcher-img" src="' + escAttr(png) + '" alt="" loading="eager" decoding="async">' +
        '</picture>' +
        '<span class="vc-launcher-balloon-tail" aria-hidden="true"></span>' +
        '<span class="vc-launcher-pulse" aria-hidden="true"></span>';
    } else {
      launcher.innerHTML = chatIcon;
    }
  }

  function fetchTeaserConfig() {
    const body = JSON.stringify({
      bot_public_id: cfg.botId,
      origin: location.origin,
      locale: cfg.locale,
      visitor_id: 'teaser-' + Math.random().toString(36).slice(2, 10),
    });
    fetch(`${cfg.apiUrl}/api/v1/widget/handshake`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    })
      .then(r => {
        if (r.ok) return r.json();
        // A bot that is not answering (paused, or a trial that ended) must not
        // leave a launcher on the customer's page that opens onto an error.
        // Only these two answers remove it — a network blip, a rate limit or a
        // 5xx keeps the widget, because hiding it then would lose real visitors.
        if (r.status === 403) {
          r.json().then((j: any) => {
            if (j?.error === 'bot_not_available' || j?.error === 'trial_ended') destroy();
          }).catch(() => { /* keep the widget */ });
        }
        return null;
      })
      .then(data => {
        if (!data?.bot) return;

        // ────────────────────────────────────────────────────────────────
        // THE PANEL IS THE SOURCE OF TRUTH FOR APPEARANCE (25/09/2026).
        //
        // The embed snippet now carries only src + data-bot-id + data-locale:
        // it identifies the bot, it does not style it. Everything visual comes
        // from here so the owner can change it without the customer editing
        // their site — which for most SMBs means calling their web developer.
        //
        // Older snippets still ship data-color / data-position /
        // data-launcher-style. Those simply lose: the server value overwrites
        // them. Before shipping this, every live site was audited and its panel
        // aligned to what it actually renders today, so this overwrite is a
        // no-op everywhere rather than a surprise redesign.
        //
        // Cost of applying it after mount is a brief flicker (~200ms) when the
        // snippet disagrees. Delaying the whole mount until the handshake
        // returns would trade that for a slower time-to-interactive on every
        // page load, which is the worse deal.
        // ────────────────────────────────────────────────────────────────
        let needsRepaint = false;

        const serverColor = data.bot.color as string | undefined;
        if (typeof serverColor === 'string' && /^#[0-9a-fA-F]{6}$/.test(serverColor) && serverColor !== cfg.color) {
          cfg.color = serverColor;
          needsRepaint = true;
        }

        const POSITIONS = ['bottom-right','bottom-left','bottom-center','top-right','top-left','top-center'];
        const serverPosition = data.bot.position as Position | undefined;
        if (typeof serverPosition === 'string' && POSITIONS.indexOf(serverPosition) !== -1 && serverPosition !== cfg.position) {
          cfg.position = serverPosition;
          needsRepaint = true;
        }

        // Launcher style for ALL values, not just 'avatar' as before — that
        // asymmetry meant a bot moved from avatar back to bubble in the panel
        // kept showing the avatar until the customer edited their HTML.
        const serverLauncherStyle = data.bot.launcher_style as WidgetConfig['launcherStyle'] | undefined;
        const serverLauncherAvatar = (data.bot.launcher_avatar ?? null) as string | null;
        let launcherChanged = false;
        if (serverLauncherAvatar !== cfg.launcherAvatar) {
          cfg.launcherAvatar = serverLauncherAvatar;
          launcherChanged = true;
        }
        if (typeof serverLauncherStyle === 'string' && serverLauncherStyle !== cfg.launcherStyle) {
          cfg.launcherStyle = serverLauncherStyle;
          launcherChanged = true;
        }

        // Styles interpolate cfg.color AND cfg.position, so one rebuild covers
        // both; the launcher's markup is separate and repainted on its own.
        if (needsRepaint) {
          const styleEl = shadow && shadow.querySelector('style');
          if (styleEl) styleEl.textContent = buildStyles();
        }
        if (launcherChanged || needsRepaint) renderLauncherContent();
        try {
          localStorage.setItem(LOOK_KEY, JSON.stringify({
            color: cfg.color, position: cfg.position,
            launcherStyle: cfg.launcherStyle, launcherAvatar: cfg.launcherAvatar,
          }));
        } catch { /* cache only */ }

        // Balloon — only if the visitor hasn't dismissed it. The sequence (welcome →
        // review score → price claim) wins; older servers send only teaser_text.
        let teaserDismissed = false;
        teaserDismissed = teaserDismissedRecently();
        const text = data.bot.teaser_text;
        const delay = data.bot.teaser_delay_ms ?? 3000;
        const seq = parseSequence(data.bot.teaser_sequence);
        if (!teaserDismissed && !isOpen) {
          if (seq.length) {
            if (teaserTimer) clearTimeout(teaserTimer);
            teaserTimer = setTimeout(() => startSequence(seq), delay);
          } else if (typeof text === 'string' && text.length > 0) {
            scheduleTeaser(text, delay);
          }
        }
        // Auto-open chat after delay — per-bot config. Skip if visitor has
        // explicitly closed the chat in this browser before (we set a flag
        // in close() the first time).
        const autoOpenDelay = data.bot.auto_open_delay_ms;
        if (typeof autoOpenDelay === 'number' && autoOpenDelay > 0 && !isOpen) {
          let closedBefore = false;
          try { closedBefore = !!localStorage.getItem(`vistochat:closed-by-user:${cfg.botId}`); } catch {}
          let openedToday = false;
          try { openedToday = Date.now() - Number(localStorage.getItem(AUTO_OPENED_KEY) || 0) < AUTO_OPEN_EVERY_MS; } catch {}
          // Phones: never open by itself (owner, 28/09/2026). On a phone the chat
          // covers the whole screen, so an unasked-for open hides the page the
          // visitor came to read; the balloon is the invitation there.
          const isPhone = (() => { try { return window.matchMedia('(max-width: 480px)').matches; } catch { return false; } })();
          if (!closedBefore && !openedToday && !isPhone) {
            // A running balloon sequence gets to finish first — opening the chat
            // mid-sequence would hide its last (and strongest) message. And never
            // open over a visitor who is reading the balloon right now.
            const willPlaySeq = !teaserDismissed && seq.length > 1;
            const seqEnd = willPlaySeq ? delay + seq.length * SEQ_STEP_MS : 0;
            const tryAutoOpen = () => {
              if (isOpen) return;
              if (seqPaused) { setTimeout(tryAutoOpen, 3000); return; }
              try { if (window.matchMedia('(max-width: 480px)').matches) return; } catch { /* no-op */ }
              try { localStorage.setItem(AUTO_OPENED_KEY, String(Date.now())); } catch { /* no-op */ }
              openChat(true);
            };
            setTimeout(tryAutoOpen, Math.max(autoOpenDelay, seqEnd));
          }
        }
      })
      .catch(() => { /* silent — best-effort */ });
  }

  function ensureFrame() {
    if (frame || !shadow) return;

    const wrap = document.createElement('div');
    wrap.className = 'vc-frame-wrap';

    skeleton = document.createElement('div');
    skeleton.className = 'vc-skeleton';
    skeleton.innerHTML =
      '<div class="vc-spinner"></div>' +
      '<div class="vc-skeleton-text">' + ui('loading') + '</div>';
    wrap.appendChild(skeleton);

    frame = document.createElement('iframe');
    frame.className = 'vc-frame';
    frame.title = 'VistoChat';
    frame.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads');
    frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    frame.setAttribute('allow', 'microphone');
    const params = new URLSearchParams({
      origin: location.origin,
      locale: cfg.locale,
      theme: darkMode ? 'dark' : 'light',
    });
    if (cfg.userJwt) params.set('user_jwt', cfg.userJwt);
    frame.src = `${cfg.apiUrl}/embed/${encodeURIComponent(cfg.botId)}?${params.toString()}`;
    frame.addEventListener('load', () => {
      if (skeleton) skeleton.classList.add('gone');
      isReady = true;
      // Send the parent page context to the iframe (used when the bot has
      // content_aware turned on — server gates whether it actually uses it).
      try {
        postToFrame({ type: 'page-context', context: capturePageContext() });
      } catch { /* ignore */ }
      emit('ready');
    });
    frame.addEventListener('error', () => {
      if (skeleton) {
        skeleton.innerHTML = '<div class="vc-skeleton-text">' + ui('failed') + '</div>';
      }
      emit('error', { message: 'iframe_load_failed' });
    });
    wrap.appendChild(frame);
    shadow.appendChild(wrap);
  }

  function onIframeMessage(e: MessageEvent) {
    if (!frame) return;
    try {
      const apiOrigin = new URL(cfg.apiUrl).origin;
      if (e.origin !== apiOrigin) return;
    } catch { return; }
    if (e.source !== frame.contentWindow) return;
    const data = e.data;
    if (!data || typeof data !== 'object' || data.__vc !== true) return;

    switch (data.type) {
      case 'close':            close(); break;
      case 'message:user':     emit('message:user', { text: data.text }); break;
      case 'message:bot':      emit('message:bot',  { text: data.text }); break;
      case 'lead:captured':    emit('lead:captured', data.lead || {}); break;
      case 'resize':           /* iframe asked to resize — handle here */ break;
      case 'set-animation': {
        const valid: AnimationStyle[] = ['slide-up', 'fade', 'bounce', 'none'];
        if (typeof data.value === 'string' && (valid as string[]).includes(data.value)) {
          currentAnimation = data.value as AnimationStyle;
          // Re-apply styles so the new transforms take effect on the NEXT
          // open/close. Don't animate the in-flight state — would feel jarring.
          if (shadow) {
            const styleEl = shadow.querySelector('style');
            if (styleEl) styleEl.textContent = buildStyles();
          }
        }
        break;
      }
      case 'set-teaser': {
        const text = typeof data.text === 'string' ? data.text.slice(0, 150) : '';
        const delay = typeof data.delay_ms === 'number' && data.delay_ms >= 0 && data.delay_ms <= 60000
                    ? data.delay_ms : 3000;
        // Skip if no text, already dismissed by user, or chat is open.
        if (!text || isOpen || seqItems.length) break;
        if (teaserDismissedRecently()) break;
        scheduleTeaser(text, delay);
        break;
      }
    }
  }

  function scheduleTeaser(text: string, delay: number) {
    if (teaserTimer) clearTimeout(teaserTimer);
    teaserTimer = setTimeout(() => showTeaser(text), delay);
  }

  function showTeaser(text: string) {
    if (!shadow || isOpen || teaserEl) return;
    teaserEl = document.createElement('div');
    teaserEl.className = 'vc-teaser';
    teaserEl.setAttribute('role', 'status');
    teaserEl.setAttribute('aria-live', 'polite');
    // Sanitise text via textContent (avoids any HTML injection from server).
    const msg = document.createElement('span');
    msg.className = 'vc-teaser-msg';
    msg.textContent = text;
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'vc-teaser-close';
    close.setAttribute('aria-label', ui('close'));
    close.textContent = '×';
    close.addEventListener('click', (e) => {
      e.stopPropagation();
      hideTeaser(true);
    });
    teaserEl.appendChild(msg);
    teaserEl.appendChild(close);
    // Click anywhere on the bubble itself opens chat (with teaser dismiss).
    msg.addEventListener('click', () => { hideTeaser(false); open(); });
    shadow.appendChild(teaserEl);
  }

  function parseSequence(v: any): SeqItem[] {
    if (!Array.isArray(v)) return [];
    const kinds: SeqKind[] = ['welcome', 'reviews', 'prices'];
    return v
      .filter((x: any) => x && kinds.indexOf(x.kind) !== -1 && typeof x.text === 'string' && x.text.trim())
      .slice(0, 5)
      .map((x: any) => ({
        kind: x.kind as SeqKind,
        text: String(x.text).slice(0, 160),
        ask: typeof x.ask === 'string' && x.ask.trim() ? String(x.ask).slice(0, 80) : null,
      }));
  }

  function trackTeaser(event: 'shown' | 'click') {
    const it = seqItems[seqIndex];
    if (!it) return;
    try {
      fetch(`${cfg.apiUrl}/api/v1/widget/teaser-event`, {
        method: 'POST',
        keepalive: true,
        // text/plain keeps this a "simple" request — no CORS preflight.
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({ bot_public_id: cfg.botId, kind: it.kind, event }),
      }).catch(() => { /* measurement only */ });
    } catch { /* measurement only */ }
  }

  function startSequence(items: SeqItem[]) {
    if (!shadow || isOpen || teaserEl || !items.length) return;
    seqItems = items;
    seqIndex = 0;
    teaserEl = document.createElement('div');
    teaserEl.className = 'vc-teaser vc-seq';
    teaserEl.setAttribute('role', 'status');
    teaserEl.setAttribute('aria-live', 'polite');
    const body = document.createElement('div');
    body.className = 'vc-seq-body';
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'vc-teaser-close';
    close.setAttribute('aria-label', ui('close'));
    close.textContent = '×';
    close.addEventListener('click', (e) => { e.stopPropagation(); hideTeaser(true); });
    teaserEl.appendChild(body);
    teaserEl.appendChild(close);
    // Any click on the balloon opens the chat — that is the whole point of it.
    teaserEl.addEventListener('click', () => {
      trackTeaser('click');
      emit('teaser:click', { kind: seqItems[seqIndex]?.kind });
      hideTeaser(false);
      open();
    });
    // Never swap the text while someone is reading or about to click it.
    teaserEl.addEventListener('mouseenter', pauseSequence);
    teaserEl.addEventListener('mouseleave', resumeSequence);
    teaserEl.addEventListener('focusin', pauseSequence);
    teaserEl.addEventListener('focusout', resumeSequence);
    shadow.appendChild(teaserEl);
    renderSeqItem(false);
  }

  function renderSeqItem(animate: boolean) {
    const body = teaserEl?.querySelector('.vc-seq-body') as HTMLElement | null;
    const it = seqItems[seqIndex];
    if (!body || !it) return;
    const paint = () => {
      body.textContent = '';
      const msg = document.createElement('span');
      msg.className = 'vc-teaser-msg' + (it.kind === 'welcome' ? '' : ' vc-seq-strong');
      msg.textContent = it.text;
      body.appendChild(msg);
      if (it.ask) {
        const ask = document.createElement('button');
        ask.type = 'button';
        ask.className = 'vc-seq-ask';
        ask.textContent = '\u{1F4AC} ' + it.ask;
        body.appendChild(ask);
      }
      teaserEl?.setAttribute('data-kind', it.kind);
    };
    let reduced = false;
    try { reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { /* no-op */ }
    if (animate && !reduced) {
      body.classList.add('vc-seq-out');
      setTimeout(() => { paint(); body.classList.remove('vc-seq-out'); }, 220);
    } else {
      paint();
    }
    trackTeaser('shown');
    if (seqIndex < seqItems.length - 1) scheduleNextItem(SEQ_STEP_MS);
  }

  function scheduleNextItem(ms: number) {
    if (seqTimer) clearTimeout(seqTimer);
    seqRemaining = ms;
    seqStartedAt = Date.now();
    seqTimer = setTimeout(() => {
      seqTimer = null;
      if (!teaserEl || isOpen) return;
      seqIndex++;
      renderSeqItem(true);
    }, ms);
  }

  function pauseSequence() {
    if (!seqTimer) return;
    clearTimeout(seqTimer);
    seqTimer = null;
    seqRemaining = Math.max(1500, seqRemaining - (Date.now() - seqStartedAt));
    seqPaused = true;
  }

  function resumeSequence() {
    if (!seqPaused) return;
    seqPaused = false;
    if (teaserEl && seqIndex < seqItems.length - 1) scheduleNextItem(seqRemaining);
  }

  function hideTeaser(persist: boolean) {
    if (teaserTimer) { clearTimeout(teaserTimer); teaserTimer = null; }
    if (teaserEl) {
      teaserEl.remove();
      teaserEl = null;
    }
    if (persist) {
      try { localStorage.setItem(TEASER_DISMISSED_KEY, String(Date.now())); } catch { /* no-op */ }
    }
    if (seqTimer) { clearTimeout(seqTimer); seqTimer = null; }
    seqItems = [];
    seqIndex = 0;
    seqPaused = false;
  }

  // ---- Public API methods ----
  function open() { openChat(false); }
  function openChat(auto: boolean) {
    if (isOpen) return;
    // A visitor who opens the chat has seen the balloon — hide it for a while.
    // The AUTOMATIC open is not the visitor's action: hide it now, remember nothing.
    if (teaserEl) hideTeaser(!auto);
    ensureFrame();
    requestAnimationFrame(() => {
      const wrap = shadow?.querySelector('.vc-frame-wrap') as HTMLElement | null;
      if (wrap) {
        wrap.classList.add('open');
        // Force reflow to make transition fire
        void wrap.offsetWidth;
        isOpen = true;
        if (launcher) launcher.setAttribute('aria-label', ui('closeChat'));
        emit('opened');
        // Focus first focusable element inside iframe (postMessage'd)
        postToFrame({ type: 'focus' });
      }
    });
  }
  function close() {
    if (!isOpen) return;
    const wrap = shadow?.querySelector('.vc-frame-wrap') as HTMLElement | null;
    if (wrap) wrap.classList.remove('open');
    isOpen = false;
    if (launcher) launcher.setAttribute('aria-label', ui('open'));
    // Remember that the visitor explicitly closed the chat — used to avoid
    // auto-open on next page load. Click-launcher-open clears this on next visit.
    try { localStorage.setItem(`vistochat:closed-by-user:${cfg.botId}`, '1'); } catch {}
    emit('closed');
  }
  function toggle() { isOpen ? close() : open(); }
  // Escape on the host page closes an open chat (audit Β8). Inside the iframe
  // the embed page does the same and posts 'close'.
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && isOpen) close(); });

  function send(text: string) {
    if (!isReady) {
      // Queue: open first, then forward when ready
      const onReady = () => {
        api.off('ready', onReady);
        postToFrame({ type: 'send', text });
      };
      api.on('ready', onReady);
      open();
      return;
    }
    postToFrame({ type: 'send', text });
  }

  function destroy() {
    close();
    if (teaserTimer) clearTimeout(teaserTimer);
    teaserTimer = null;
    if (seqTimer) clearTimeout(seqTimer);
    seqTimer = null;
    teaserEl = null;
    window.removeEventListener('message', onIframeMessage);
    if (mediaQueryListener) {
      const mq = window.matchMedia('(max-width: 480px)');
      mq.removeEventListener?.('change', mediaQueryListener);
    }
    host?.remove();
    host = shadow = launcher = frame = skeleton = null;
    listeners.clear();
    isOpen = false; isReady = false;
    delete window.VistoChat;
  }

  function on(event: EventName, cb: (d?: any) => void) {
    if (!listeners.has(event)) listeners.set(event, new Set());
    listeners.get(event)!.add(cb);
  }
  function off(event: EventName, cb: (d?: any) => void) {
    listeners.get(event)?.delete(cb);
  }

  function configUpdate(updates: Partial<WidgetConfig>) {
    Object.assign(cfg, updates);
    // If color/position changed and host is mounted, re-apply styles
    if (shadow) {
      const styleEl = shadow.querySelector('style');
      if (styleEl) styleEl.textContent = buildStyles();
    }
  }

  const api: VistoChatAPI = {
    open, close, toggle, send, destroy, on, off,
    isReady: () => isReady,
    config: configUpdate,
  };
  window.VistoChat = api;

  // Repeat visitor: start from the appearance the server sent last time, so the
  // launcher face is requested at mount instead of after the handshake. The
  // handshake still overwrites it if the panel changed since.
  try {
    const look = JSON.parse(localStorage.getItem(LOOK_KEY) || 'null');
    if (look && typeof look === 'object') {
      if (typeof look.color === 'string' && /^#[0-9a-fA-F]{6}$/.test(look.color)) cfg.color = look.color;
      if (typeof look.position === 'string') cfg.position = look.position;
      if (typeof look.launcherStyle === 'string') cfg.launcherStyle = look.launcherStyle;
      if (look.launcherAvatar === null || typeof look.launcherAvatar === 'string') cfg.launcherAvatar = look.launcherAvatar;
    }
  } catch { /* no cache — first visit */ }

  // Mount as soon as <body> exists — NOT at DOMContentLoaded. The launcher is
  // position:fixed in the top layer, so it does not need the rest of the page;
  // waiting for the whole HTML to be parsed held the face back until 9.3s on
  // Azur (heavy synchronous scripts before its footer), measured 28/09/2026.
  // Loaded from <head> (async), <body> does not exist yet: wait for it.
  if (document.body) {
    mount();
  } else {
    const waitBody = () => {
      if (document.body) { mount(); return; }
      if (document.readyState === 'loading') setTimeout(waitBody, 50);
      else mount();
    };
    document.addEventListener('DOMContentLoaded', () => { if (!host) mount(); }, { once: true });
    setTimeout(waitBody, 0);
  }
}


/**
 * Extract the host page's URL, title and main text content for the
 * "Content Aware" feature. Server-side decides whether to actually use it
 * (based on the bot's content_aware flag), so this is opportunistic.
 */
function capturePageContext(): { url: string; title: string; content: string } {
  const root: HTMLElement | null =
    document.querySelector('main') ||
    document.querySelector('article') ||
    document.querySelector('[role="main"]') ||
    document.body;

  // Clone so we can prune noisy elements without mutating the host page.
  const clone = root ? (root.cloneNode(true) as HTMLElement) : null;
  if (clone) {
    clone.querySelectorAll('script, style, noscript, nav, header, footer, aside, [aria-hidden="true"]')
      .forEach(n => n.remove());
  }
  const raw = (clone?.innerText || '').replace(/[ \t]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim();

  return {
    url: location.href,
    title: document.title || '',
    content: raw.slice(0, 8000), // generous cap; server clips again to its own limits
  };
}

function readConfigFromScript(): WidgetConfig | null {
  // Find the script tag that loaded us (prefer currentScript, fall back to query)
  const script = (document.currentScript as HTMLScriptElement | null)
    ?? document.querySelector<HTMLScriptElement>('script[data-bot-id]');
  if (!script) {
    console.error('[VistoChat] script tag with data-bot-id not found');
    return null;
  }
  const botId = script.dataset.botId;
  if (!botId) {
    console.error('[VistoChat] missing data-bot-id attribute');
    return null;
  }

  const htmlLang = document.documentElement.lang?.split('-')[0];
  return {
    botId,
    apiUrl:        script.dataset.apiUrl        ?? 'https://app.vistochat.com',
    position:      (script.dataset.position as Position)        ?? 'bottom-right',
    launcherStyle: (script.dataset.launcherStyle as WidgetConfig['launcherStyle']) ?? 'bubble',
    // Hydrated from handshake (data.bot.launcher_avatar) before the launcher
    // mounts; allowing data attribute as a manual override is intentional —
    // some customers want to A/B test their own face URL without going through
    // the dashboard. Validation happens server-side at handshake time so this
    // dataset value can be ignored by the server's bot config.
    launcherAvatar: script.dataset.launcherAvatar ?? null,
    // Only a hex colour reaches the stylesheet (audit Β3): the value is pasted
    // into CSS, so 'red; width:500px' would otherwise inject rules.
    color:         /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(script.dataset.color ?? '') ? script.dataset.color! : '#101475',
    locale:        script.dataset.locale        ?? htmlLang ?? 'el',
    userJwt:       script.dataset.userJwt       ?? null,
    launcher:      (script.dataset.launcher as WidgetConfig['launcher']) ?? 'auto',
    teaserText:    script.dataset.teaserText    ?? null,
  };
}
